<template>
    <div style="position: relative">
        <div ref="map" id="ol-map-container" class="w-100" :style="{height: mapHeight}"></div>
        <div v-if="tooltip" v-html="tooltipHtml"
             class="map-tooltip" :style="{left: tooltipX + 'px', top: tooltipY + 'px'}">
        </div>
        <b-button v-if="plot" class="map-button" variant="primary" @click="importPlot()">导入为标绘</b-button>
        <prompt-dialog ref="promptDialog" title="请输入地标名称"></prompt-dialog>
    </div>
</template>

<script>
import * as $ from "jquery";
import * as Html from "@/utils/html";
import VectorLayer from "ol/layer/Vector";
import VectorSource from "ol/source/Vector";
import {fromLonLat, gcj02towgs84} from "@/olMap/coordUtils";
import {fromLonLat as olFromLonLat} from 'ol/proj';
import OpenLayersMap from "ol/Map";
import {defaults as defaultInteractions} from "ol/interaction";
import {defaults as defaultControls} from "ol/control";
import ScaleLineControl from "@/olMap/control/ScaleLine";
import MapTypeControl from "@/olMap/control/MapType";
import RoadTypeControl from "@/olMap/control/RoadType";
import View from "ol/View";
import {getLayers} from "@/olMap/mapLayers";
import {createFeature, createPlotStyle, initPlotTypes} from "@/olMap/featureUtils";
import BrokerWechat from "@/services/brokerWechat";
import * as MsgBox from "@/utils/msgBox";
import * as olExtent from "ol/extent";
import PromptDialog from "@/components/PromptDialog";

export default {
    name: "WechatPosition",
    components: {PromptDialog},
    data() {
        return {
            mapHeight: "100vh",
            tooltip: "",
            tooltipX: 0,
            tooltipY: 0,
            plot: null,
        };
    },
    methods: {
        getClientSize() {
            return [
                window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth,
                window.innerHeight || document.documentElement.clientHeight || document.body.clientHeight,
            ];
        },
        onPointerMove(e) {
            if (e.dragging) {
                return;
            }
            this.lastPointerPixel = this.map.getEventPixel(e.originalEvent);
            this.updateTooltip();
        },
        updateTooltip() {
            if (this.lastPointerPixel) {
                let feature = this.getTooltipFeature(this.lastPointerPixel);
                if (feature) {
                    this.tooltip = feature.get('tooltip');
                    this.tooltipX = this.lastPointerPixel[0] + 7;
                    this.tooltipY = this.lastPointerPixel[1] + 20;
                } else if (this.tooltip) {
                    this.tooltip = "";
                }
                return feature;
            }
        },
        getTooltipFeature(pixel) {
            let feature = null;
            this.map.forEachFeatureAtPixel(pixel, function (x) {
                feature = feature || (x.get('tooltip') ? x : null);
            }, {hitTolerance: 5});
            return feature;
        },
        async load(id, qywx) {
            let message;
            if (qywx) {
                message = await BrokerWechat.loadQywxPosition(id);
            } else {
                message = await BrokerWechat.loadPosition(id);
            }
            let plot = message && message.location;
            if (plot) {
                plot.coordinate = olFromLonLat([plot.y, plot.x]);
                let view = this.map.getView();
                view.setCenter(plot.coordinate);
                view.setZoom(plot.scale || 11);
                this.updatePlot(plot);
            } else {
                MsgBox.alert(this, "未找到该地标位置");
            }
        },
        setViewport(coordinate) {
            let size = this.getClientSize();
            let px = Math.max(50, Math.round(size[0] * 0.1));
            let py = Math.max(50, Math.round(size[1] * 0.1));
            this.map.getView().fit(olExtent.boundingExtent([coordinate]), {
                padding: [py, px, py, px], maxZoom: 11,
            });
        },
        updatePlot(plot) {
            plot.wgs84 = gcj02towgs84(plot.y, plot.x);
            plot.name = plot.poiname || plot.label || "";
            plot.type = '地标';
            let tooltipLines = [`名称：${plot.name}`];
            if (plot.poiname && plot.label) {
                tooltipLines.push(`地址：${plot.label}`);
                plot.name += `\n地址：${plot.label}`;
            }
            tooltipLines.push(`经度：${plot.wgs84[0]}`, `纬度：${plot.wgs84[1]}`);
            let feature = createFeature(plot.coordinate, createPlotStyle(plot), {
                plot: plot, tooltip: tooltipLines.join("\n")
            });
            this.plotLayer.setSource(new VectorSource({features: [feature]}));
            this.plot = plot;
        },
        async importPlot() {
            let name = await this.$refs.promptDialog.modal();
            if (name) {
                let plot = {name, type: "地标", alias: [], lon: this.plot.wgs84[0], lat: this.plot.wgs84[1]};
                let result = (await BrokerWechat.addPlot(plot)) || {};
                if (result.msg === "name_occupied") {
                    await MsgBox.alert(this, `已有名称为“${name}”的标绘，请换一个名称`);
                    this.importPlot();
                } else if (result.msg === "alias_occupied") {
                    await MsgBox.alert(this, `已有别名为“${name}”的标绘，请换一个名称`);
                    this.importPlot();
                } else if (result.msg === "success") {
                    await MsgBox.alert(this, "标绘导入成功");
                    window.parent.postMessage({action: "on_plot_added", name: result.plotName || name}, origin);
                }
            }
        },
    },
    computed: {
        tooltipHtml() {
            return Html.format(this.tooltip);
        },
    },
    async mounted() {
        $(document.body).css("overflow", "hidden");

        this.plotLayer = new VectorLayer({source: new VectorSource({features: []})});
        let center = fromLonLat([119.511392, 32.21177]);
        let zoom = 11;
        this.map = new OpenLayersMap({
            target: 'ol-map-container',
            interactions: defaultInteractions({
                altShiftDragRotate: false,
                pinchRotate: false,
                doubleClickZoom: false,
            }),
            controls: defaultControls().extend([
                new ScaleLineControl({units: 'metric'}),
                new MapTypeControl(),
                new RoadTypeControl(),
            ]),
            loadTilesWhileInteracting: true,
            view: new View({center, zoom, maxZoom: 19}),
            layers: getLayers().concat([this.plotLayer]),
        });
        this.map.on("pointermove", e => this.onPointerMove(e));

        await initPlotTypes();
        let id = +this.$route.query["id"] || 0;
        let qywx = +this.$route.query["qywx"] || 0;
        if (id) {
            await this.load(id, qywx);
        }
    },
    created() {
        this.sizeHandler = () => this.mapHeight = this.getClientSize()[1] + "px";
        window.onresize = this.sizeHandler;
        this.sizeHandler();
    },
    destroyed() {
        $(document.body).css("overflow", "");
    },
}
</script>

<style scoped>
.map-tooltip {
    position: absolute;
    top: 0;
    left: 0;
    padding: .25em .5em;
    background: white;
    border: 1px solid rgba(0, 0, 0, 0.2);
    border-radius: 5px;
    z-index: 100;
}

.map-button {
    position: absolute;
    right: 10px;
    top: 10px;
}
</style>

<style>
.ol-map-type {
    bottom: 10px;
    left: 10px;
}

.ol-road-type {
    bottom: 10px;
    left: 160px;
}

.ol-control.ol-map-type, .ol-control.ol-road-type {
    display: flex;
}

.ol-control.ol-map-type button, .ol-control.ol-road-type button {
    width: auto;
    height: auto;
    line-height: normal;
    font-size: 18px;
    font-weight: normal;
    padding: 5px 10px;
}

.ol-control.ol-map-type button.selected, .ol-control.ol-road-type button.selected {
    text-decoration: none;
    background-color: rgba(0, 60, 136, .8);
}

.ol-scale-line {
    top: auto;
    left: auto;
    right: 10px;
    bottom: 10px;
}

.ol-zoom {
    left: auto;
    top: auto;
    right: 10px;
    bottom: 40px;
}

.ol-zoom button {
    font-size: 1.5rem;
}

@media (max-width: 512px) {
    .ol-road-type {
        bottom: 55px;
        left: 10px;
    }
}

.ol-tooltip {
    position: relative;
    background: rgba(0, 0, 0, 0.5);
    border-radius: 4px;
    color: white;
    padding: 4px 8px;
    opacity: 0.7;
    white-space: nowrap;
    font-size: 12px;
}

.ol-tooltip-measure {
    opacity: 1;
    font-weight: bold;
}

.ol-tooltip-static {
    background-color: orangered;
    color: white;
    border: 1px solid white;
}

.ol-tooltip-measure:before,
.ol-tooltip-static:before {
    border-top: 6px solid rgba(0, 0, 0, 0.5);
    border-right: 6px solid transparent;
    border-left: 6px solid transparent;
    content: "";
    position: absolute;
    bottom: -6px;
    margin-left: -7px;
    left: 50%;
}

.ol-tooltip-static:before {
    border-top-color: orangered;
}

.ol-tooltip-static-close-btn {
    font-size: 1.4rem;
    line-height: 0.8rem;
    height: 1rem;
    cursor: pointer;
}

.ol-tooltip-static-close-btn:hover {
}

.ol-tooltip-static-close-btn:active {
}

.ol-event-transparent {
    pointer-events: none;
}

.ol-event-solid {
    pointer-events: auto;
}

.ol-circle {
    background: transparent;
    width: 10px;
    height: 10px;
    border: 1px solid black;
    border-radius: 5px;
}
</style>
